import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildProfilePayload,
  isProfileEffectivelyEmpty,
  normalizeProfile,
  normalizeTextList,
  unique,
} from "../utils/profileMappers";
import { HEALTH_PROBLEMS } from "../constants/profileOptions";
import {
  fetchCurrentUser,
  fetchProfile,
  updateProfile,
} from "../services/profileService";

const initialProfileState = {
  displayName: "",
  gender: "",
  ageRange: "",
  height: "",
  startWeight: "",
  startWeightDate: "",
  goalWeight: "",
  goalDate: "",
  diagnosis: "",
  healthIssues: [],
  comorbidities: "",
  otherMeds: "",
  objectives: [],
};

export function useProfileForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [savedProfile, setSavedProfile] = useState(null);
  const [userName, setUserName] = useState("");
  const [healthQuery, setHealthQuery] = useState("");
  const [isHealthOpen, setIsHealthOpen] = useState(false);
  const [profile, setProfile] = useState(initialProfileState);

  const healthInputRef = useRef(null);
  const healthDropdownWrapperRef = useRef(null);
  const messageTimerRef = useRef(null);
  const isSavingRef = useRef(false);

  const imc = useMemo(() => {
    const h = Number(String(profile.height).replace(",", "."));
    const w = Number(String(profile.startWeight).replace(",", "."));

    if (!h || !w || h <= 0 || w <= 0) return "--";

    const heightM = h / 100;
    return (w / (heightM * heightM)).toFixed(1);
  }, [profile.height, profile.startWeight]);

  const firstName = useMemo(() => {
    const name = (profile.displayName || userName || "").trim();
    return name ? name.split(" ")[0] : "";
  }, [profile.displayName, userName]);

  const profileInitial = useMemo(() => {
    return firstName ? firstName.charAt(0).toUpperCase() : "C";
  }, [firstName]);

  const healthOptions = useMemo(() => {
    const query = healthQuery.trim().toLowerCase();
    const selected = new Set((profile.healthIssues || []).map((item) => item.toLowerCase()));
    const available = HEALTH_PROBLEMS.filter(
      (problem) => !selected.has(problem.toLowerCase())
    );

    if (!query) return available.slice(0, 12);

    return available
      .filter((problem) => problem.toLowerCase().includes(query))
      .slice(0, 12);
  }, [healthQuery, profile.healthIssues]);

  const canAddMoreHealthIssues = (profile.healthIssues || []).length < 5;

  useEffect(() => {
    async function loadProfile() {
      try {
        const [profileData, meData] = await Promise.all([
          fetchProfile().catch(() => ({})),
          fetchCurrentUser().catch(() => ({})),
        ]);

        if (isSavingRef.current) return;

        const serverProfile = profileData || {};
        const normalized = normalizeProfile(serverProfile);

        setProfile(normalized);
        setSavedProfile(normalized);
        setIsEditing(isProfileEffectivelyEmpty(serverProfile));

        const me = meData || {};
        setUserName(me?.name || me?.user?.name || me?.fullName || "");
      } catch {
        if (isSavingRef.current) return;

        const fresh = normalizeProfile({});
        setProfile(fresh);
        setSavedProfile(fresh);
        setIsEditing(true);
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, []);

  useEffect(() => {
    const onDown = (event) => {
      if (!isHealthOpen) return;

      const wrapper = healthDropdownWrapperRef.current;
      if (wrapper && !wrapper.contains(event.target)) {
        setIsHealthOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [isHealthOpen]);

  useEffect(() => {
    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
        messageTimerRef.current = null;
      }
    };
  }, []);

  const clearMessageSoon = () => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
    }

    messageTimerRef.current = setTimeout(() => {
      setMessage({ text: "", type: "" });
      messageTimerRef.current = null;
    }, 3500);
  };

  const showSuccessMessage = (text) => {
    if (!text) return;
    setMessage({ text, type: "success" });
    clearMessageSoon();
  };

  const showErrorMessage = (text) => {
    if (!text) return;
    setMessage({ text, type: "error" });
    clearMessageSoon();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      isSavingRef.current = true;

      const payload = buildProfilePayload(profile);
      const serverProfile = await updateProfile(payload);
      const normalized = normalizeProfile(serverProfile);

      setSavedProfile(normalized);
      setProfile(normalized);
      setIsEditing(false);

      try {
        localStorage.setItem("profile", JSON.stringify(serverProfile || {}));
        window.dispatchEvent(new Event("profileUpdated"));
      } catch {
        // sem ação
      }

      showSuccessMessage("Informações atualizadas com sucesso.");
    } catch (error) {
      const backendMsg = error?.response?.data?.message;
      const status = error?.response?.status;

      const finalMsg = backendMsg
        ? backendMsg
        : status
          ? `Erro ao salvar (HTTP ${status}). Tente novamente.`
          : "Erro ao salvar. Tente novamente.";

      showErrorMessage(finalMsg);
    } finally {
      setSaving(false);

      setTimeout(() => {
        isSavingRef.current = false;
      }, 300);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setMessage({ text: "", type: "" });
    setHealthQuery("");
    setIsHealthOpen(false);

    if (savedProfile) {
      setProfile(savedProfile);
    }
  };

  const addHealthIssue = (value) => {
    const formatted = String(value || "").trim();
    if (!formatted) return;

    setProfile((prev) => {
      const current = normalizeTextList(prev.healthIssues);
      if (current.length >= 5) return prev;

      const next = unique([...current, formatted]).slice(0, 5);

      return {
        ...prev,
        healthIssues: next,
        comorbidities: next.join(", "),
      };
    });

    setHealthQuery("");
    setIsHealthOpen(true);
    requestAnimationFrame(() => healthInputRef.current?.focus());
  };

  const removeHealthIssue = (value) => {
    const formatted = String(value || "").trim();

    setProfile((prev) => {
      const current = normalizeTextList(prev.healthIssues);
      const next = current.filter((item) => item !== formatted);

      return {
        ...prev,
        healthIssues: next,
        comorbidities: next.join(", "),
      };
    });

    requestAnimationFrame(() => healthInputRef.current?.focus());
  };

  const handleHealthKeyDown = (event) => {
    if (!canAddMoreHealthIssues) return;

    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const typed = healthQuery.trim();
      if (typed) addHealthIssue(typed);
    }

    if (event.key === "Escape") {
      setIsHealthOpen(false);
    }
  };

  return {
    loading,
    saving,
    message,
    isEditing,
    setIsEditing,
    profile,
    setProfile,
    userName,
    profileInitial,
    imc,
    healthQuery,
    setHealthQuery,
    isHealthOpen,
    setIsHealthOpen,
    healthInputRef,
    healthDropdownWrapperRef,
    healthOptions,
    canAddMoreHealthIssues,
    handleSave,
    cancelEdit,
    addHealthIssue,
    removeHealthIssue,
    handleHealthKeyDown,
    showSuccessMessage,
  };
}